export type PlayerId = string;
export type SystemId = number;
export type FleetId = number;
export type CharacterId = number;

export enum SystemType {
  Imperial,
  Wildspace,
  ThroneWorld,
  ImperialCore
}

export enum PlanetType {
  Terran = 'terran',
  Desert = 'desert',
  Ice = 'ice',
  Volcanic = 'volcanic',
  GasGiant = 'gas_giant'
}

export interface StarSystem {
  id: SystemId;
  name: string;
  owner: PlayerId | 'NPC' | 'Unclaimed';
  type: SystemType;
  adjacentSystems: SystemId[];
  position: { x: number; y: number };
  planetType: PlanetType;
  population: number;
  maxPopulation: number;
  hasShipyard: boolean;
  constructionQueue?: Array<{
    // Server now assigns a stable id to each job
    jobId?: string;
    type: 'ship' | 'shipyard' | 'repair';
    completionTick: number;
    // For UI only: duration of the job when scheduled (optional)
    durationTicks?: number;
    fleetId?: FleetId;
    shipClass?: ShipClass;
    targetFleetId?: FleetId;
    // Present if server includes it; used as fallback key
    queueIndex?: number;
  }>;

  // Siege and fortifications
  maxFortification?: number;
  fortification?: number;
  underSiegeBy?: PlayerId | null;

  // NPC faction metadata when owned by NPC
  npcFactionId?: string | null;
  isStronghold?: boolean;
}

export type ShipClass = 'frigate' | 'destroyer' | 'cruiser' | 'capital';

export type ShipInstance = {
  id: number;
  class: ShipClass;
  hp: number;
  maxHp: number;
};

export interface Fleet {
  id: FleetId;
  owner: PlayerId | 'NPC';
  ships: ShipInstance[];
  power: number;
  maxPower: number;
  location: SystemId;
  
  // Multi-tick movement support
  inTransit?: boolean;
  origin?: SystemId;
  destination?: SystemId;
  arrivalTick?: number;
  
  // Multi-tick combat support
  inCombat?: boolean;
  combatTarget?: FleetId;  // Primary target in multi-fleet battles
  lastCombatTick?: number; // Track when combat was last processed
  isOriginalAttacker?: boolean; // True if this fleet moved in to attack
  combatRound?: number; // Internal counter for round number

  // Special actions
  isBombardingPopulation?: boolean;
}

export enum CharacterRole {
  Unassigned,
  Governor,
  Admiral
}

export interface Character {
  id: CharacterId;
  name: string;
  role: CharacterRole;
  assignment: SystemId | FleetId | null;
  // Stats
  admin: number;    // Governing effectiveness
  military: number; // Fleet command effectiveness
  // Progress counters (optional, informational)
  adminXP?: number;
  militaryXP?: number;
}

export interface FleetStatus {
  id: number;
  owner: string;
  power: number;
  maxPower: number;
  powerLost: number; // Damage taken this round
}

export interface CombatResult {
  type?: string; // 'combat_round', 'combat_end'
  tick: number;
  round: number; // Combat round number
  attacker: string;
  defenders: string[];
  location: string;
  
  // Detailed fleet information before combat
  attackerFleet: FleetStatus; // Aggregated attacker side for compatibility
  attackerFleets?: FleetStatus[]; // All attacker fleets (optional)
  defenderFleets: FleetStatus[];
  
  // Damage information
  damageToDefenders: number; // Damage dealt by attacker
  damageToAttacker: number; // Damage dealt by defenders
  // Optional visualization hints
  attackerComposition?: Partial<Record<ShipClass, number>>;
  defenderComposition?: Partial<Record<ShipClass, number>>;
  damageFlowsFromAttacker?: { fromClass: ShipClass; toClass: ShipClass; amount: number }[];
  damageFlowsFromDefender?: { fromClass: ShipClass; toClass: ShipClass; amount: number }[];
  
  // Legacy fields for backward compatibility
  damageDealt: number;
  shipsLost: {
    attacker: number;
    defenders: number;
  };
  
  // End of combat information
  winner?: string; // Only present at the end of combat
  systemCaptured?: boolean;
}

export interface Player {
  id: PlayerId;
  name: string;
  resources: {
    credits: number;
    influence: number;
  };
  character: Character;
  // Cosmetics
  color?: string;
  fleetIcon?: string;
  portraitStyle?: string;
}

export interface VoteRecord { yea: number; nay: number; }
export type LawEffect =
  | { kind: 'IncomeMultiplier'; creditsMultiplier?: number; influenceMultiplier?: number }
  | { kind: 'BuildTimeMultiplier'; fleetMultiplier?: number; shipyardMultiplier?: number; repairMultiplier?: number }
  | { kind: 'TravelTimeMultiplier'; imperial?: number; wildspace?: number; imperialCore?: number }
  | { kind: 'NoBombardment' }
  | { kind: 'FortDamageMultiplier'; systemId: SystemId; multiplier: number }
  | { kind: 'WritTrespass'; systemId: SystemId; exceptDuringCombat?: boolean };

export interface ActiveLaw {
  id: number;
  title: string;
  description: string;
  enactedTick: number;
  expiresTick?: number;
  effect: LawEffect;
}

export interface SenateMotion {
  id: number;
  title: string;
  type:
    | 'GrantResources'
    | 'GrantInfluence'
    | 'ImperialTax'
    | 'ShipbuildingDrive'
    | 'IncomeBoost'
    | 'PropagandaCampaign'
    | 'RepairSubsidies'
    | 'ShipyardExpansion'
    | 'OpenHyperlanes'
    | 'WildspaceInterdiction'
    | 'NoBombardment'
    | 'SafePassageCorridors'
    | 'WarBonds'
    | 'CulturalRenaissance'
    | 'ArmsLimitation'
    | 'EmergencyRepairs'
    | 'DockyardOvertime'
    | 'PropagandaBlitzPlus'
    | 'IndustrialAusterity'
    | 'PlanetaryShieldMandate'
    | 'WritTrespass';
  details: string;
  params?: any;
  votes: { [playerId: string]: VoteRecord };
}

export interface SenateVoteRecord {
  id: number;
  title: string;
  type: SenateMotion['type'];
  details: string;
  resolvedTick: number;
  outcome: 'Passed' | 'Failed';
  yea: number;
  nay: number;
}

export interface SenateState {
  currentMotion: SenateMotion | null;
  upcomingMotion: SenateMotion | null;
  ticksUntilResolution: number;
  activeLaws: ActiveLaw[];
  pastVotes: SenateVoteRecord[];
}

// Frontend version of GameState, with Maps converted to arrays
export interface GameState {
  tick: number;
  systems: StarSystem[];
  players: Player[];
  fleets: Fleet[];
  senate: SenateState;
  winner: PlayerId | null;
  capitalSystemController: PlayerId | null;
  ticksCapitalSystemHeld: number;
  // Optional map metadata for rendering clusters and labels
  mapSeed?: number;
  mapMeta?: any;
}
